import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useScenes } from "@/components/studio/SceneLibrary";
import { RichEditor } from "@/components/rich/RichEditor";
import { ListPick, Empty } from "@/components/studio/widgets/Pickers";

const SceneEditor = ({ uid, scene }) => {
  const qc = useQueryClient();
  const [title, setTitle] = useState(scene.title || "");
  const [content, setContent] = useState(scene.content || "");
  const [state, setState] = useState("saved");
  const pending = useRef(null);
  const t = useRef(null);
  const flush = async () => {
    const body = pending.current;
    if (!body) return;
    if ("title" in body && !body.title.trim()) { delete body.title; if (!Object.keys(body).length) { pending.current = null; setState("dirty"); return; } }
    pending.current = null;
    setState("saving");
    try { await api.patch(`/scenes/${scene.id}`, body); setState(pending.current ? "dirty" : "saved"); qc.invalidateQueries({ queryKey: ["scenes", uid] }); }
    catch (e) { pending.current = { ...body, ...(pending.current || {}) }; setState("error"); toast.error(errMsg(e)); }
  };
  const queue = (patch) => {
    pending.current = { ...(pending.current || {}), ...patch };
    setState("dirty");
    clearTimeout(t.current);
    t.current = setTimeout(flush, 800);
  };
  useEffect(() => () => { clearTimeout(t.current); flush(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-2" data-testid="scene-widget-editor">
      <Input value={title} onChange={(e) => { setTitle(e.target.value); queue({ title: e.target.value }); }} placeholder="Scene title" maxLength={200} className="h-8 font-display text-base" data-testid="scene-widget-title" />
      <RichEditor value={content} onChange={(md) => { if (md === content) return; setContent(md); queue({ content: md }); }} placeholder="Notes for this scene: beats, mood, who's there…" minH="min-h-[120px]" testid="scene-widget-content" />
      <div className="text-right text-[10px] text-[#A8A090]" data-testid="scene-widget-status">{!title.trim() ? `Notes ${state === "saved" ? "saved" : "saving…"} · add a title to save it` : { saved: "Saved", saving: "Saving…", dirty: "Unsaved changes", error: "Couldn't save" }[state]}</div>
    </div>
  );
};

export const SceneWidget = ({ uid, cfg, set }) => {
  const { data: scenes = [], isLoading } = useScenes(uid);
  const qc = useQueryClient();
  const scene = scenes.find((s) => s.id === cfg.id);
  const create = async () => {
    try {
      const { data } = await api.post(`/universes/${uid}/scenes`, { title: "New scene", content: "" });
      qc.invalidateQueries({ queryKey: ["scenes", uid] });
      set({ id: data.id });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="scene-widget">
      <div className="flex items-center gap-1.5">
        <div className="min-w-0 flex-1"><ListPick items={scenes.map((s) => ({ id: s.id, name: s.title || "Untitled scene" }))} value={cfg.id} onChange={(id) => set({ id })} testid="scene-widget-pick" placeholder="Pick a scene…" /></div>
        <button onClick={create} title="New scene note" className="flex h-8 items-center gap-1 rounded border border-[#2D3442] px-2 text-[11px] text-[#F59E0B] hover:bg-[#1A202C]" data-testid="scene-widget-new"><Plus className="h-3.5 w-3.5" />New</button>
      </div>
      {scene ? <SceneEditor key={scene.id} uid={uid} scene={scene} />
        : <Empty testid="scene-widget-empty">{isLoading ? "Loading scenes…" : scenes.length ? "Pick a scene to keep its notes beside your chapter." : "No scene notes yet. Create one with New."}</Empty>}
    </div>
  );
};
